import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";

import { IDog, IDogStats } from "@eagerdog/interfaces";

import { apiService } from "../../../services/api.service";
import Button from "../../../components/Button/Button";

import "./StatsTab.scss";

interface IProps {
  dog: IDog;
  isOwner: boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SEGMENT_COLORS = ["#F25A2D", "#3b82f6", "#8b5cf6", "#f59e0b", "#14b8a6", "#ec4899"];

const money = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return "—";
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (d: Date | string): string => {
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const fmtDateLong = (d: Date | string): string => {
  const date = new Date(d);
  return isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const pctOf = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

/**
 * Stats tab — lifetime activity dashboard for one dog (web).
 * Same payload as mobile: GET /dogs/:id/stats (owner) or /public/dog/:id/stats.
 */
const StatsTab: React.FC<IProps> = ({ dog, isOwner }) => {
  const [stats, setStats] = useState<IDogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = isOwner ? await apiService.getDogStats(dog._id) : await apiService.getPublicDogStats(dog._id);
      setStats(data);
    } catch (e) {
      const err = e as AxiosError<{ message?: string }>;
      setError(err.response?.data?.message || "Unable to load stats");
    } finally {
      setLoading(false);
    }
  }, [dog._id, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  const busiestMonth = useMemo(() => {
    if (!stats) return null;
    const max = stats.by_month.reduce((best, m) => (m.runs > best.runs ? m : best), stats.by_month[0]);
    return max && max.runs > 0 ? max : null;
  }, [stats]);

  if (loading && !stats) {
    return (
      <div className="statsTab loading">
        <div className="loadingMessage">Loading stats...</div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="statsTab empty">
        <div className="emptyState">
          <div className="emptyIcon">📊</div>
          <h2>Stats unavailable</h2>
          <p>{error}</p>
          <Button onClick={load}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  if (stats.total_runs === 0) {
    return (
      <div className="statsTab empty">
        <div className="emptyState">
          <div className="emptyIcon">📊</div>
          <h2>No activity yet</h2>
          <p>
            {isOwner
              ? `Stats build up as ${dog.call_name} enters events on Eager Dog or you add runs in the Ribbon Room.`
              : `${dog.call_name} has no recorded activity yet.`}
          </p>
        </div>
      </div>
    );
  }

  const r = stats.results;
  const scored = r.qualified + r.not_qualified;
  const inv = stats.investment;
  const thisYear = new Date().getFullYear();
  const yearRow = (y: number) => stats.by_year.find((row) => row.year === y);
  const clubTotal = stats.by_sanctioning_club.reduce((n, b) => n + b.runs, 0);
  const maxSportRuns = stats.by_sport[0]?.runs || 1;
  const maxYearRuns = Math.max(1, ...stats.by_year.map((y) => y.runs));
  const maxMonthRuns = Math.max(1, ...stats.by_month.map((m) => m.runs));

  return (
    <div className="statsTab">
      {/* ---- Hero -------------------------------------------------------- */}
      <div className="statsCard hero">
        <div className="heroText">
          <h3>{dog.call_name}'s career</h3>
          <p>
            {stats.first_run_date ? `Trialing since ${fmtDateLong(stats.first_run_date)}` : "No runs yet"}
            {stats.last_run_date ? ` · Last run ${fmtDate(stats.last_run_date)}` : ""}
          </p>
          {stats.self_reported_runs > 0 && (
            <p className="footnote">
              Includes {stats.self_reported_runs} self-reported run{stats.self_reported_runs === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div className="tiles">
          <Tile value={stats.total_events} label="Events" />
          <Tile value={stats.total_runs} label="Runs" />
          <Tile value={r.q_rate === null ? "—" : `${r.q_rate}%`} label="Q Rate" accent />
          <Tile value={stats.titles_earned} label="Titles" />
        </div>
      </div>

      <div className="statsGridTwo">
        {/* ---- Activity ---------------------------------------------------- */}
        <div className="statsCard">
          <h4>Activity by sport</h4>
          {stats.by_sport.map((b) => (
            <div key={b.key} className="barRow">
              <div className="barLabelRow">
                <span className="barLabel">{b.label}</span>
                <span className="barValue">
                  {b.runs} run{b.runs === 1 ? "" : "s"}
                  {b.qualified > 0 ? ` · ${b.qualified} Q` : ""}
                </span>
              </div>
              <div className="barTrack">
                <div className="barFill" style={{ width: `${Math.max(3, (b.runs / maxSportRuns) * 100)}%` }} />
              </div>
            </div>
          ))}

          {stats.by_sanctioning_club.length > 1 && (
            <>
              <h4 className="spaced">By organization</h4>
              <div className="segmented">
                {stats.by_sanctioning_club.map((b, i) => (
                  <div
                    key={b.key}
                    className="segment"
                    style={{ flex: b.runs, background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                    title={`${b.label}: ${b.runs}`}
                  />
                ))}
              </div>
              <div className="legend">
                {stats.by_sanctioning_club.map((b, i) => (
                  <span key={b.key} className="legendItem">
                    <i style={{ background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} />
                    {b.label} {pctOf(b.runs, clubTotal)}%
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="statsCard">
          <h4>Runs by year</h4>
          <div className="columns tall">
            {stats.by_year.map((y) => (
              <div key={y.year} className="column" title={`${y.year}: ${y.runs} runs, ${y.qualified} Q, ${y.events} events`}>
                <span className="columnValue">{y.runs}</span>
                <div className="columnTrack">
                  <div
                    className={`columnFill${y.year === thisYear ? " accent" : ""}`}
                    style={{ height: `${Math.max(4, (y.runs / maxYearRuns) * 100)}%` }}
                  />
                </div>
                <span className="columnLabel">{y.year}</span>
              </div>
            ))}
          </div>
          {yearRow(thisYear) && yearRow(thisYear - 1) && (
            <p className="footnote">
              {thisYear}: {yearRow(thisYear)!.runs} runs vs {yearRow(thisYear - 1)!.runs} in {thisYear - 1}
            </p>
          )}

          <h4 className="spaced">Runs by month</h4>
          <div className="columns">
            {stats.by_month.map((m) => (
              <div key={m.month} className="column" title={`${MONTHS[m.month - 1]}: ${m.runs}`}>
                <div className="columnTrack short">
                  <div
                    className={`columnFill${busiestMonth?.month === m.month ? " accent" : ""}`}
                    style={{ height: `${Math.max(m.runs > 0 ? 6 : 2, (m.runs / maxMonthRuns) * 100)}%` }}
                  />
                </div>
                <span className="columnLabel small">{MONTHS[m.month - 1]}</span>
              </div>
            ))}
          </div>
          {busiestMonth && (
            <p className="footnote">
              Busiest month: {MONTHS[busiestMonth.month - 1]} ({pctOf(busiestMonth.runs, stats.total_runs)}% of runs)
            </p>
          )}
        </div>

        {/* ---- Results ----------------------------------------------------- */}
        <div className="statsCard">
          <h4>Results</h4>
          {scored > 0 || r.absent_or_excused > 0 ? (
            <>
              <div className="segmented">
                {r.qualified > 0 && <div className="segment" style={{ flex: r.qualified, background: "#10b981" }} title={`Qualified: ${r.qualified}`} />}
                {r.not_qualified > 0 && <div className="segment" style={{ flex: r.not_qualified, background: "#ef4444" }} title={`NQ: ${r.not_qualified}`} />}
                {r.absent_or_excused > 0 && <div className="segment" style={{ flex: r.absent_or_excused, background: "#d1d5db" }} title={`Absent/Excused: ${r.absent_or_excused}`} />}
              </div>
              <div className="legend">
                <span className="legendItem"><i style={{ background: "#10b981" }} />Qualified {r.qualified}</span>
                <span className="legendItem"><i style={{ background: "#ef4444" }} />NQ {r.not_qualified}</span>
                {r.absent_or_excused > 0 && <span className="legendItem"><i style={{ background: "#d1d5db" }} />Absent/Exc {r.absent_or_excused}</span>}
              </div>
            </>
          ) : (
            <p className="muted">No scored runs yet.</p>
          )}
          <div className="tiles">
            <Tile value={r.current_q_streak} label="Current Q streak" accent={r.current_q_streak > 0} />
            <Tile value={r.longest_q_streak} label="Longest Q streak" />
            {r.unscored > 0 && <Tile value={r.unscored} label="Awaiting results" />}
          </div>

          {stats.personal_bests.length > 0 && (
            <>
              <h4 className="spaced">Personal bests</h4>
              {stats.personal_bests.map((pb) => (
                <div key={pb.key} className="row">
                  <div className="rowText">
                    <div className="rowTitle">{pb.label}</div>
                    <div className="rowSub">
                      {fmtDate(pb.date)}
                      {pb.event_name ? ` · ${pb.event_name}` : ""}
                    </div>
                  </div>
                  <div className="rowValue">{pb.value}</div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* ---- Passport ---------------------------------------------------- */}
        <div className="statsCard">
          <h4>Trial passport</h4>
          <div className="tiles">
            <Tile value={stats.passport.states.length} label="States" accent />
            <Tile value={stats.passport.clubs} label="Clubs" />
            <Tile value={stats.passport.judges} label="Judges" />
          </div>
          {stats.passport.states.length > 0 && (
            <div className="chips">
              {stats.passport.states.map((s) => (
                <span key={s} className="chip">{s}</span>
              ))}
            </div>
          )}
          {stats.passport.top_club && (
            <p className="footnote">
              Home club: {stats.passport.top_club.name} ({stats.passport.top_club.runs} runs)
            </p>
          )}
          {stats.passport.top_judge && (
            <p className="footnote">
              Most seen judge: {stats.passport.top_judge.name} ({stats.passport.top_judge.runs} runs)
            </p>
          )}
        </div>

        {/* ---- Investment (owner only) ------------------------------------- */}
        {isOwner && inv && (
          <div className="statsCard investment">
            <h4>Investment</h4>
            <div className="tiles">
              <Tile value={money(inv.total_cents)} label="Entry fees" accent />
              <Tile value={money(inv.this_year_cents)} label={`In ${thisYear}`} />
              <Tile value={money(inv.cost_per_run_cents)} label="Per run" />
              <Tile value={money(inv.cost_per_q_cents)} label="Per Q" />
              <Tile value={inv.events_paid} label="Events paid" />
            </div>
            <p className="footnote">
              Entry fees you paid through Eager Dog for {dog.call_name}. Excludes add-ons; refunds are netted out. Only
              visible to you.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Tile: React.FC<{ value: string | number; label: string; accent?: boolean }> = ({ value, label, accent }) => (
  <div className={`tile${accent ? " accent" : ""}`}>
    <div className="tileValue">{value}</div>
    <div className="tileLabel">{label}</div>
  </div>
);

export default StatsTab;
